import React, { memo, useEffect, useRef, useState } from 'react';
import Modal from 'components/shared/Modal/Modal';
import { useDispatch, useSelector } from 'react-redux';
import {
  doGetTwoHopAlignmentStatusInstances,
  updateActionsPageTwoHopCancelStatusDictionary,
  updateBundlesDictionary,
  updateDevicesDictionary,
  updateStatus
} from 'redux/actions';
import { LOADING_CIRCLE, Status } from 'common/utils/constants';
import { mapDeviceSearchResult, searchDevices } from 'apis/SearchDevices';
import { cancelTwoHopAlignment } from 'apis/twoHopApis/CancelTwoHopAlignment';
import { getCommissioningStatus } from 'apis/commissioningApis/GetCommissioningStatus';
import { getDUAndSUsFromBundleMembers, isObjectEmpty, createApiPolling, getErrorMessage } from 'common/utils/helpers';
import HeatmapImageCollectingScreen from 'components/features/HeatmapImageCollectingScreen/HeatmapImageCollectingScreen';
import CommissionErrorBox from 'components/features/Commissioning/CommissionErrorBox/CommissionErrorBox';
import ActionTwoHopModalFooterContainer from './ActionTwoHopModalFooterContainer/ActionTwoHopModalFooterContainer';
import ActionTwoHopModalHeaderContainer from './ActionTwoHopModalHeaderContainer/ActionTwoHopModalHeaderContainer';
import ActionTwoHopModalContentContainer from './ActionTwoHopModalContentContainer/ActionTwoHopModalContentContainer';
import ActionTwoHopModalBannerContainer from './ActionTwoHopModalBannerContainer/ActionTwoHopModalBannerContainer';
import './ActionTwoHopProgressViewModal.css';

const ActionTwoHopProgressViewModal = (props) => {
  const [isGetTwoHopAlignmentCalled, setIsGetTwoHopAlignmentCalled] = useState(false);
  const [getTwoHopAlignmentStatusError, setGetTwoHopAlignmentStatusError] = useState(null);
  const [isSearchDevicesCalled, setIsSearchDevicesCalled] = useState(false);
  const [searchTwoHopDevicesError, setSearchTwoHopDevicesError] = useState(null);
  const [isGetTwoHopRecBeamInfoCalled, setIsGetTwoHopRecBeamInfoCalled] = useState(false);
  const [postTwoHopRecBeamInfoError, setPostTwoHopRecBeamInfoError] = useState(null);
  const [podOneDevices, setPodOneDevices] = useState([null, null, null]);
  const [podTwoDevices, setPodTwoDevices] = useState([null, null, null]);
  const [podNameOne, setPodNameOne] = useState(null);
  const [podNameTwo, setPodNameTwo] = useState(null);
  const [isCollectingFiles, setIsCollectingFiles] = useState(false);
  const [updatedAction, setUpdatedAction] = useState(props.action);

  const progressRunner = useRef(null);
  const mounted = useRef(false);
  const stopCancelPolling = useRef(false);
  const dispatch = useDispatch();

  const commissioningId = props.action?.commissioningId;

  const twoHopProgressStatusName = `GET-TwoHopAlignmentStatusInstances-${commissioningId}`;
  const twoHopProgressStatus = useSelector((store) => store.statuses?.[twoHopProgressStatusName]);
  const searchTwoHopDevicesStatusName = `POST-SearchTwoHopDevices-${commissioningId}`;
  const searchTwoHopDevicesStatus = useSelector((store) => store.statuses?.[searchTwoHopDevicesStatusName]);
  const getAnyRasterRecordStatusName = 'GET-AnyRasterRecord';
  const getAnyRasterRecordStatus = useSelector((store) => store.statuses?.[getAnyRasterRecordStatusName]);
  const getTwoHopAlignmentBeamInfoStatusName = `GET-TwoHopAlignmentBeamInfo-${commissioningId}`;
  const getTwoHopAlignmentBeamInfoStatus = useSelector(
    (store) => store.statuses?.[getTwoHopAlignmentBeamInfoStatusName]
  );
  const postTwoHopAlignmentBeamInfoStatusName = `POST-TwoHopAlignmentBeamInfo-${commissioningId}`;
  const postTwoHopAlignmentBeamInfoStatus = useSelector(
    (store) => store.statuses?.[postTwoHopAlignmentBeamInfoStatusName]
  );

  // update mounted ref
  useEffect(() => {
    if (mounted.current === false) {
      mounted.current = true;
    }

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    // retrieve and start polling two-hop status instance
    if (!isGetTwoHopAlignmentCalled && updatedAction && twoHopProgressStatus !== Status.PENDING) {
      const cb_success = (res) => {
        setIsGetTwoHopAlignmentCalled(true);
        if (updatedAction?.runtimeStatus === 'Running') {
          progressRunner.current = pollTwoHopStatusInstances();
        }
      };
      const cb_failed = (error) => {
        console.log(error.message);
        setGetTwoHopAlignmentStatusError(error.message);
      };
      commissioningId && dispatch(doGetTwoHopAlignmentStatusInstances(commissioningId, cb_success, cb_failed));
    }

    // retrieve two pods' devices
    if (updatedAction?.input && !isSearchDevicesCalled) {
      const du1 = updatedAction?.input?.pivot1DU ?? updatedAction?.input?.du1 ?? 'Unknown';
      const du2 = updatedAction?.input?.pivot2DU ?? updatedAction?.input?.du2 ?? 'Unknown';
      let newPodOneDevices = [null, null, null];
      let newPodTwoDevices = [null, null, null];
      let newPodNameOne = null;
      let newPodNameTwo = null;

      const searchDevicesV2Body = {
        size: 2,
        coalesceBundles: true,
        filters: {
          text: '',
          deviceId: [du1, du2]
        }
      };

      dispatch(updateStatus(searchTwoHopDevicesStatusName, Status.PENDING));
      searchDevices(searchDevicesV2Body, false, 'v2')
        .then((result) => {
          const formattedResult = mapDeviceSearchResult(result);

          dispatch(updateDevicesDictionary(formattedResult.devices, true));
          dispatch(updateBundlesDictionary(formattedResult.bundles));

          return result;
        })
        .then((res) => {
          if (res?.bundles && !isObjectEmpty(res.bundles)) {
            res.bundles.forEach((bundle) => {
              const devices = getDUAndSUsFromBundleMembers(bundle.members);
              if (devices?.du?.deviceId === du1) {
                newPodNameOne = bundle.name || bundle.code;
                newPodOneDevices = [du1, devices?.su1?.deviceId ?? null, devices?.su2?.deviceId ?? null];
              } else if (devices?.du?.deviceId === du2) {
                newPodNameTwo = bundle.name || bundle.code;
                newPodTwoDevices = [du2, devices?.su1?.deviceId ?? null, devices?.su2?.deviceId ?? null];
              }
            });
            setPodNameOne(newPodNameOne);
            setPodNameTwo(newPodNameTwo);
            setPodOneDevices(newPodOneDevices);
            setPodTwoDevices(newPodTwoDevices);
            dispatch(updateStatus(searchTwoHopDevicesStatusName, Status.SUCCESS));
          } else {
            setSearchTwoHopDevicesError('Not Found');
            dispatch(updateStatus(searchTwoHopDevicesStatusName, Status.FAILURE));
          }
        })
        .catch((error) => {
          console.error(error);
          setSearchTwoHopDevicesError(error.message);
          dispatch(updateStatus(searchTwoHopDevicesStatusName, Status.FAILURE));
        });
      setIsSearchDevicesCalled(true);
    }
  }, [isGetTwoHopAlignmentCalled, updatedAction, isSearchDevicesCalled]);

  // poll two-hop cancel status and two-hop action instance if the action is not completed
  useEffect(() => {
    if (updatedAction.runtimeStatus !== 'Completed') {
      pollTwoHopCancelStatus();
      pollTwoHopAction();
    }
  }, []);

  // stop cancel polling and two-hop status instance polling when action is completed
  useEffect(() => {
    if (updatedAction?.runtimeStatus === 'Completed') {
      stopCancelPolling.current = true;
      clearTimeoutAndInterval();
    }
  }, [updatedAction]);

  // poll two-hop cancel action status
  const pollTwoHopCancelStatus = async () => {
    const checkCallback = (result) => {
      dispatch(updateActionsPageTwoHopCancelStatusDictionary(commissioningId, result));
      if (result?.entityState?.status === 'completed' || !mounted.current || stopCancelPolling.current) {
        return true;
      }
      return false;
    };
    const poll = createApiPolling(cancelTwoHopAlignment, checkCallback, { delay: 8000 });
    const requestBody = { action: 'get', id: commissioningId };

    try {
      await poll(requestBody);
      console.log('Stopped polling Two-Hop Cancel status.');
    } catch (error) {
      console.error(error);
    }
  };

  // poll two-hop action instance
  const pollTwoHopAction = async () => {
    const checkCallback = (result) => {
      setUpdatedAction(result);
      if (result?.runtimeStatus === 'Completed' || !mounted.current) {
        return true;
      }
      return false;
    };
    const poll = createApiPolling(getCommissioningStatus, checkCallback, { delay: 8000 });

    try {
      await poll(commissioningId);
      console.log('Stopped polling Two-Hop Action.');
    } catch (error) {
      console.error(error);
    }
  };

  const clearTimeoutAndInterval = () => {
    progressRunner?.current && clearInterval(progressRunner.current);
  };

  // poll two-hop status instances
  const pollTwoHopStatusInstances = () => {
    return setInterval(() => {
      dispatch(doGetTwoHopAlignmentStatusInstances(commissioningId));
    }, 8000);
  };

  const onCloseScreen = () => {
    dispatch(updateStatus(twoHopProgressStatusName, Status.NONE));
    dispatch(updateStatus(searchTwoHopDevicesStatusName, Status.NONE));
    dispatch(updateStatus(getAnyRasterRecordStatusName, Status.NONE));
    dispatch(updateStatus(getTwoHopAlignmentBeamInfoStatusName, Status.NONE));
    dispatch(updateStatus(postTwoHopAlignmentBeamInfoStatusName, Status.NONE));
    clearTimeoutAndInterval();
    props.setTwoHopProgressViewModalOpen(false);

    if (props.action?.runtimeStatus !== updatedAction?.runtimeStatus) {
      props.actionsPageThis && props.actionsPageThis.setState({ refreshActionList: true });
    }
  };

  let overlay = null;
  if (
    (!isGetTwoHopAlignmentCalled && twoHopProgressStatus === Status.PENDING) ||
    (!isSearchDevicesCalled && searchTwoHopDevicesStatus === Status.PENDING) ||
    (!isGetTwoHopRecBeamInfoCalled && getTwoHopAlignmentBeamInfoStatus === Status.PENDING) ||
    postTwoHopAlignmentBeamInfoStatus === Status.PENDING ||
    getAnyRasterRecordStatus === Status.PENDING
  ) {
    overlay = (
      <div id="ActionTwoHopProgressViewModal_Loading_Overlay" className="ActionTwoHopProgressViewModal_Loading_Overlay">
        {LOADING_CIRCLE}
      </div>
    );
  } else if (!isGetTwoHopAlignmentCalled && getTwoHopAlignmentStatusError != null) {
    overlay = (
      <CommissionErrorBox
        title={`Get Two-Hop Status Error: ${getTwoHopAlignmentStatusError}`}
        message={getErrorMessage(getTwoHopAlignmentStatusError)}
        onClose={() => onCloseScreen()}
      />
    );
  } else if (getAnyRasterRecordStatus === Status.FAILURE) {
    overlay = (
      <CommissionErrorBox
        title={'Get Raster Record Error:'}
        message={'Something went wrong. Please try again later.'}
        onClose={() => onCloseScreen()}
      />
    );
  } else if (postTwoHopAlignmentBeamInfoStatus === Status.FAILURE) {
    overlay = (
      <CommissionErrorBox
        title={`Update Beam Info Error: ${postTwoHopRecBeamInfoError}`}
        message={getErrorMessage(postTwoHopRecBeamInfoError)}
        onClose={() => onCloseScreen()}
      />
    );
  }

  return (
    <Modal
      width={1440}
      height={970}
      close={() => onCloseScreen()}
      animateX={false}
      global
      noPadding
      className={'ActionTwoHopProgressViewModal_VisibleOverflow'}
    >
      {overlay}
      {isCollectingFiles && (
        <HeatmapImageCollectingScreen
          onClose={() => {
            setIsCollectingFiles(false);
          }}
        />
      )}
      <div id="ActionTwoHopProgressViewModal_Inner_Container" className="ActionTwoHopProgressViewModal_Inner_Container">
        <ActionTwoHopModalHeaderContainer
          podOneDevices={podOneDevices}
          podTwoDevices={podTwoDevices}
          podNameOne={podNameOne}
          podNameTwo={podNameTwo}
          searchTwoHopDevicesStatus={searchTwoHopDevicesStatus}
        />
        <ActionTwoHopModalBannerContainer action={updatedAction} />
        <ActionTwoHopModalContentContainer
          action={props.action} // passing immutable props.action to avoid table flickering
          isGetTwoHopAlignmentCalled={isGetTwoHopAlignmentCalled}
          isGetTwoHopRecBeamInfoCalled={isGetTwoHopRecBeamInfoCalled}
          setIsGetTwoHopRecBeamInfoCalled={setIsGetTwoHopRecBeamInfoCalled}
          setIsCollectingFiles={setIsCollectingFiles}
          setPostTwoHopRecBeamInfoError={setPostTwoHopRecBeamInfoError}
        />
        <ActionTwoHopModalFooterContainer
          action={updatedAction}
          actionsPageThis={props.actionsPageThis}
          onCloseScreen={onCloseScreen}
        />
      </div>
    </Modal>
  );
};

export default memo(ActionTwoHopProgressViewModal);
